import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../Navbar";
import {
  getProNotifications,
  markProNotificationsRead,
  markProNotificationRead,
  formatDate,
  formatMoney,
} from "../../services/proSystemApi";
import "./Pro.css";

// =====================================================
// PRO NOTIFICATIONS  (Step 20)
//
// "Separate notifications for Payments, Likes, Comments, Shares, Payouts."
//
// Separate means separate: this list is its own collection with its own five
// types, and none of it appears in the app's free notification screen. That is
// not a UI choice — widening the existing Notification model's type list would
// have changed a table the free notification screen, the navbar badge and the
// push bridge all read.
// =====================================================

const TABS = [
  { key: "", label: "All", icon: "🔔" },
  { key: "payment", label: "Payments", icon: "💰" },
  { key: "like", label: "Likes", icon: "❤️" },
  { key: "comment", label: "Comments", icon: "💬" },
  { key: "share", label: "Shares", icon: "🔗" },
  { key: "payout", label: "Payouts", icon: "🏦" },
];

const ICON = {
  payment: "💰",
  like: "❤️",
  comment: "💬",
  share: "🔗",
  payout: "🏦",
};

function ProNotifications() {
  const navigate = useNavigate();

  const [tab, setTab] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async (type) => {
    try {
      setLoading(true);
      setData(await getProNotifications(type));
      setError(null);
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to load notifications");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(tab);
  }, [load, tab]);

  const markAll = async () => {
    try {
      await markProNotificationsRead();
      await load(tab);
    } catch (err) {
      /* the list simply stays as it is */
    }
  };

  const open = async (item) => {
    if (!item.isRead) {
      try {
        await markProNotificationRead(item._id);
      } catch {
        /* opening must not depend on the read flag being saved */
      }
    }

    if (item.link) navigate(item.link);
  };

  return (
    <div className="pro-page-wrap">
      <Navbar />

      <div className="pro-container pro-container-narrow">
        <header className="pro-hero pro-hero-compact">
          <div className="pro-hero-badge">🔔 PRO NOTIFICATIONS</div>
          <h1 className="pro-hero-title">Your Pro activity</h1>
          {data?.unreadCount > 0 && (
            <p className="pro-hero-sub">{data.unreadCount} unread</p>
          )}
        </header>

        <div className="pro-tabs pro-tabs-scroll" role="tablist">
          {TABS.map((t) => (
            <button
              key={t.key || "all"}
              type="button"
              role="tab"
              aria-selected={tab === t.key}
              className={`pro-tab${tab === t.key ? " pro-tab-active" : ""}`}
              onClick={() => setTab(t.key)}
            >
              <span aria-hidden="true">{t.icon}</span> {t.label}
              {t.key && data?.counts?.[t.key] > 0 && (
                <span className="pro-tab-count">{data.counts[t.key]}</span>
              )}
            </button>
          ))}
        </div>

        {loading && (
          <div className="pro-loading-screen">
            <div className="pro-spinner" />
            <p>Loading…</p>
          </div>
        )}

        {!loading && error && (
          <div className="pro-card pro-empty">
            <p>{error}</p>
            <button type="button" className="pro-btn pro-btn-primary" onClick={() => load(tab)}>
              Try again
            </button>
          </div>
        )}

        {!loading && !error && (
          <>
            {data.unreadCount > 0 && (
              <div className="pro-section-head pro-section-head-right">
                <button type="button" className="pro-btn pro-btn-ghost pro-btn-sm" onClick={markAll}>
                  Mark all read
                </button>
              </div>
            )}

            {data.items.length === 0 ? (
              <div className="pro-card pro-empty">
                <p>Nothing here yet.</p>
              </div>
            ) : (
              <ul className="pro-notif-list">
                {data.items.map((item) => (
                  <li key={item._id}>
                    <button
                      type="button"
                      className={`pro-notif${item.isRead ? "" : " pro-notif-unread"}`}
                      onClick={() => open(item)}
                    >
                      <span className="pro-notif-icon" aria-hidden="true">
                        {ICON[item.type] || "🔔"}
                      </span>

                      <span className="pro-notif-body">
                        <strong className="pro-notif-title">{item.title}</strong>
                        <span className="pro-notif-text">{item.message}</span>
                        <small className="pro-notif-date">{formatDate(item.createdAt)}</small>
                      </span>

                      {item.amountRupees > 0 && (
                        <span className="pro-notif-amount">
                          {formatMoney(item.amountRupees)}
                        </span>
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}

        <button
          type="button"
          className="pro-btn pro-btn-ghost pro-back"
          onClick={() => navigate("/pro-home")}
        >
          ← Pro Home
        </button>
      </div>
    </div>
  );
}

export default ProNotifications;
